import React, { useContext, useState, useCallback, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Video, OffthreadVideo } from "../video/index";
import { TimelineContext } from "../timeline-position-state";
import { useRemotionEnvironment } from "../get-environment.js";
import { ImageFrameRenderer } from "./ImageFrameRenderer";
import { DustParticles } from "./DustParticles";
const ASSET_BASE = "https://dash.animaker.com/a/u/";

const SceneContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #000;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: opacity 0.3s ease;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255,255,255,0.15);
  border-top-color: rgba(255,255,255,0.7);
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

function resolveUrl(src) {
  if (!src) return "";
  if (src === "src/sample/sample.mp4") return "/sample.mp4";
  if (src.startsWith("http") || src.startsWith("/")) return src;
  return `${ASSET_BASE}${src}`;
}

function getBackgroundStyle(background) {
  if (!background) return {};
  if (background.type === "solid" && background.color) {
    const color = Array.isArray(background.color) ? background.color[0] : background.color;
    return { background: color };
  }
  if (background.type === "gradient" && background.colors) {
    return {
      background: `linear-gradient(${background.angle || 180}deg, ${background.colors.join(", ")})`,
    };
  }
  return {};
}

export function SceneRenderer({
  scene,
  projectWidth = 800,
  projectHeight = 450,
  isNearby = true,
  isActive = false,
}) {
  const { muted, frame, setBuffering } = useContext(TimelineContext);
  const [videoReady, setVideoReady] = useState(false);
  const videoElRef = useRef(null);
  const environment = useRemotionEnvironment();
  const isRendering = environment === 'rendering';

  const video = scene.video;

  // Track global buffering status for active scene
  useEffect(() => {
    if (isActive && video?.src) {
      setBuffering(!videoReady);
    }
    return () => {
      if (isActive) {
        setBuffering(false);
      }
    };
  }, [isActive, video?.src, videoReady, setBuffering]);

  // Reset ready state when src changes
  const currentSrc = video?.src;
  const prevSrcRef = useRef(currentSrc);
  useEffect(() => {
    if (currentSrc !== prevSrcRef.current) {
      setVideoReady(false);
      prevSrcRef.current = currentSrc;
    }
  }, [currentSrc]);

  // Track video element readyState via a ref callback
  const videoRefCallback = useCallback((el) => {
    videoElRef.current = el;
    if (el) {
      // If video already has data (e.g. cached), mark ready immediately
      if (el.readyState >= 2) {
        setVideoReady(true);
      }

      const onCanPlay = () => setVideoReady(true);
      const onWaiting = () => setVideoReady(false);
      const onLoadedData = () => setVideoReady(true);

      el.addEventListener("canplay", onCanPlay);
      el.addEventListener("loadeddata", onLoadedData);
      el.addEventListener("waiting", onWaiting);

      // Cleanup stored on element for removal
      el._sceneCleanup = () => {
        el.removeEventListener("canplay", onCanPlay);
        el.removeEventListener("loadeddata", onLoadedData);
        el.removeEventListener("waiting", onWaiting);
      };
    }
  }, []);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      if (videoElRef.current?._sceneCleanup) {
        videoElRef.current._sceneCleanup();
      }
    };
  }, []);

  return (
    <SceneContainer style={getBackgroundStyle(scene.background)}>
      {/* Image frames rendering (Ken Burns slideshow) */}
      {scene.images && scene.images.length > 0 && (
        <>
          <ImageFrameRenderer
            images={scene.images}
            showVignette={true}
            showLetterbox={true}
          />
          <DustParticles seed={scene.id ? scene.id.length * 7 : 42} />
        </>
      )}

      {/* Video rendering */}
      {video && video.src && (() => {
        const src = resolveUrl(video.src);
        const left = ((video.x || 0) / projectWidth) * 100;
        const top = ((video.y || 0) / projectHeight) * 100;
        // In rendering mode, force video to fill the full stage.
        // The project dimensions match the viewport, so 100% = full frame.
        const width = isRendering ? 100 : ((video.width || projectWidth) / projectWidth) * 100;
        const height = isRendering ? 100 : ((video.height || projectHeight) / projectHeight) * 100;
        const volume = video.volume != null ? video.volume : 1;
        const fps = 30;
        const startFrom = Math.round((video.videoStart || 0) * fps);
        // Only set endAt if video is actually trimmed shorter than its full duration
        const videoDur = video.videoDuration || Infinity;
        const videoEnd = video.videoEnd != null ? video.videoEnd : videoDur;
        const endAt = videoEnd < videoDur - 0.05 ? Math.ceil(videoEnd * fps) : undefined;

        const containerStyle = {
          position: "absolute",
          left: `${left}%`,
          top: `${top}%`,
          width: `${width}%`,
          height: `${height}%`,
          overflow: "hidden",
          background: "#000",
        };

        // When scene is far from playhead, render a lightweight placeholder
        // instead of a <video> element to avoid exhausting browser resources
        if (!isNearby) {
          return (
            <div key={video.id} style={containerStyle}>
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#111",
                }}
              />
            </div>
          );
        }

        // In rendering mode use OffthreadVideo: frames are fetched as images
        // from the frame-proxy server instead of seeking a <video> element.
        // This eliminates browser connection-pool contention so multiple tabs
        // can render concurrently without timing out.
        if (isRendering && window.picmaker_proxyPort) {
          return (
            <div key={video.id} style={containerStyle}>
              <OffthreadVideo
                src={src}
                startFrom={startFrom}
                endAt={endAt}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
                volume={muted ? 0 : volume}
                muted={muted || volume === 0}
                playbackRate={video.playbackRate || 1}
              />
            </div>
          );
        }

        return (
          <div key={video.id} style={containerStyle}>
            {/* Loading spinner overlay — shown while video is buffering */}
            <LoadingOverlay $visible={!videoReady}>
              <Spinner />
            </LoadingOverlay>

            <Video
              ref={videoRefCallback}
              src={src}
              startFrom={startFrom}
              endAt={endAt}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
                willChange: "transform",
                backfaceVisibility: "hidden",
                transform: video.angle
                  ? `rotate(${video.angle}deg) translateZ(0)`
                  : "translateZ(0)",
              }}
              volume={muted ? 0 : volume}
              muted={muted || volume === 0}
              playbackRate={video.playbackRate || 1}
              crossOrigin="anonymous"
              preload="auto"
            />
          </div>
        );
      })()}
    </SceneContainer>
  );
}
